import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { ProcessDTO } from '@/types/api-types';

import IconMobileLogo from '@/assets/svg/icon-add-mobile.svg?react';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';

import { useGlobalStore } from '@/stores/globalStore';

import { USER_ROLES } from '@/constants/global-constants';
import { privateRoutes } from '@/constants/route-settings';

import { ProcessListTable } from './ProcessListTable';

export function ProcessListTab() {
  const [processToDelete, setProcessToDelete] = useState<
    ProcessDTO['uuid'] | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const { processes, fetchProcesses, deleteProcess, getAllSteps } =
    useGlobalStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchProcesses(), getAllSteps()]);
      setIsLoading(false);
    };

    init();
  }, []);

  const handleDelete = async (id: ProcessDTO['uuid']) => {
    setProcessToDelete(null);
    await deleteProcess(id);
  };

  const handleAddProcess = () => {
    navigate(
      `${privateRoutes[USER_ROLES.ADMIN.value].settings}/${
        privateRoutes[USER_ROLES.ADMIN.value].processes.add
      }`,
    );
  };

  const handleEditProcess = (process: ProcessDTO) => {
    navigate(
      `${privateRoutes[USER_ROLES.ADMIN.value].settings}/${privateRoutes[
        USER_ROLES.ADMIN.value
      ].processes.edit.replace(':uuid', process.uuid)}`,
    );
  };

  const handleCopyProcess = (id: string) => {
    navigate(
      `${privateRoutes[USER_ROLES.ADMIN.value].settings}/${privateRoutes[
        USER_ROLES.ADMIN.value
      ].processes.copy.replace(':uuid', id)}`,
    );
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={!!processToDelete}
        onClose={() => setProcessToDelete(null)}
        onConfirm={() => handleDelete(processToDelete!)}
        title="Видалити процес?"
        cancelText="Скасувати"
        confirmText="Видалити"
      />
      <div className="flex h-full flex-col rounded-md bg-white shadow-sm">
        {/* Fixed header section */}
        <div className="flex flex-shrink-0 items-center justify-between border-b pb-2 pl-8 pr-[22px] pt-4">
          <h2 className="text-2xl font-medium text-primary">Список процесів</h2>

          {isMobile ? (
            <button
              className="rounded-md bg-primary text-white"
              onClick={handleAddProcess}
            >
              <IconMobileLogo className="h-[34px] w-[34px] fill-primary" />
            </button>
          ) : (
            <Button
              className="h-[50px] w-[150px] rounded-md bg-primary text-white"
              onClick={handleAddProcess}
            >
              Додати процес
            </Button>
          )}
        </div>

        {/* Scrollable table container */}
        <div className="flex-grow overflow-auto">
          <ProcessListTable
            data={processes.filter((p) => !p.is_deleted)}
            onEdit={handleEditProcess}
            onCopy={handleCopyProcess}
            onDelete={(id) => setProcessToDelete(id as ProcessDTO['uuid'])}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  );
}
